import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { get } from "@/lib/api";
import { formatDate, formatNumber, formatUsd } from "@/lib/format";
import { formatAdminStatus } from "./adminFormat";
import { ErrorState, TableSkeleton } from "@/components/common/DataStates";
import { Input } from "@/components/ui/input";
import { Link } from "react-router-dom";

export default function PlatformOrganizationsPage() {
  const [search, setSearch] = useState("");
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["platform-admin", "organizations"],
    queryFn: () => get("/admin/organizations"),
    retry: false,
  });
  const rows = Array.isArray(data) ? data : [];
  const query = search.trim().toLocaleLowerCase();
  const visible = rows.filter((row) => [row.name, row.plan_code, row.status].some((value) => String(value ?? "").toLocaleLowerCase().includes(query)));

  return (
    <div className="min-w-0 space-y-6" data-testid="platform-organizations">
      <div>
        <p className="og-section-label">Organisations</p>
        <h2 className="mt-1 font-heading text-2xl font-semibold">Toutes les organisations</h2>
        <p className="mt-1 text-sm text-muted-foreground">Vue globale · lecture seule</p>
      </div>
      <div className="og-card min-w-0 overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
          <label className="relative block w-full max-w-sm">
            <span className="sr-only">Rechercher une organisation</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Rechercher nom, plan ou statut" className="pl-9" aria-label="Rechercher une organisation" />
          </label>
          {!isLoading && !isError && <span className="text-sm text-muted-foreground" aria-live="polite">{formatNumber(visible.length)} organisation{visible.length !== 1 ? "s" : ""}</span>}
        </div>
        {isLoading ? <TableSkeleton testId="platform-organizations-loading" />
          : isError ? <div className="p-4"><ErrorState message="Impossible de charger les organisations." onRetry={refetch} /></div>
            : rows.length === 0 ? <p className="p-6 text-sm text-muted-foreground">Aucune organisation enregistrée.</p>
              : visible.length === 0 ? <p className="p-6 text-sm text-muted-foreground">Aucune organisation ne correspond à cette recherche.</p>
                : <div className="max-w-full overflow-x-auto" role="region" aria-label="Liste des organisations" tabIndex={0}>
                  <table className="w-full min-w-[960px] text-left text-sm">
                    <thead className="bg-muted/50 text-xs text-muted-foreground">
                      <tr>
                        {["Organisation", "Statut", "Plan", "Membres", "Agents actifs", "Appels IA", "Exécutions", "Coût IA", "Créée le"].map((label) => <th key={label} scope="col" className="whitespace-nowrap px-4 py-3 font-medium">{label}</th>)}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {visible.map((row) => (
                        <tr key={row.id} className="hover:bg-muted/30">
                          <th scope="row" className="max-w-[220px] break-words px-4 py-3 font-medium"><Link className="og-focus text-primary hover:underline" to={`/app/admin/organizations/${encodeURIComponent(row.id)}`}>{row.name}</Link></th>
                          <td className="px-4 py-3">{formatAdminStatus(row.status)}</td>
                          <td className="px-4 py-3">{row.plan_code || "—"}</td>
                          <td className="px-4 py-3 tabular-nums">{formatNumber(row.members)}</td>
                          <td className="px-4 py-3 tabular-nums">{formatNumber(row.agents_enabled)}</td>
                          <td className="px-4 py-3 tabular-nums">{formatNumber(row.ai_calls_month)}</td>
                          <td className="px-4 py-3 tabular-nums">{formatNumber(row.runs_month)}</td>
                          <td className="whitespace-nowrap px-4 py-3 tabular-nums">{formatUsd(row.ai_cost_month_usd)}</td>
                          <td className="whitespace-nowrap px-4 py-3">{formatDate(row.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>}
      </div>
    </div>
  );
}